import axios, { AxiosResponse } from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import { HiringFlow, HiringFlowChannel, HiringFlowEntry, HiringFlowStep } from "@type/hiring-flow";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

// Hiring-flow templates (#3071, BE contract ourteam-backend#3641 §3).
// System rows (company_id NULL, is_system true) are locked — the BE rejects
// PUT/DELETE on them; "Save as template" from a system template always creates
// a company copy. Duplicate names get a " (2)" suffix server-side.
export interface HiringFlowTemplate {
    id: string;
    company_id: string | null;
    name: string;
    steps: HiringFlowStep[];
    channel: HiringFlowChannel | null;
    entry: HiringFlowEntry | null;
    created_by: string | null;
    is_system: boolean;
    created_at: string | null;
    updated_at: string | null;
}

function authHeaders() {
    const token = getCookie("authToken");
    return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
    };
}

/** GET /hiring-flow/templates — system templates + the caller's company templates. */
export async function getHiringFlowTemplates(): Promise<HiringFlowTemplate[]> {
    const response: AxiosResponse = await axios({
        url: `${apiURL}/hiring-flow/templates`,
        method: "GET",
        headers: authHeaders(),
        timeout: 15000,
    });
    return response.data.data || [];
}

/** POST /hiring-flow/templates — save a company template (name required). */
export async function createHiringFlowTemplate(params: {
    name: string;
    steps: HiringFlowStep[];
    channel?: HiringFlowChannel;
    entry?: HiringFlowEntry;
}): Promise<HiringFlowTemplate | undefined> {
    try {
        const response: AxiosResponse = await axios({
            url: `${apiURL}/hiring-flow/templates`,
            method: "POST",
            headers: authHeaders(),
            data: params,
            timeout: 15000,
        });
        return response.data.data;
    } catch (error) {
        catchErrorLog(`Failed to save hiring-flow template: ${error}`);
        throw error;
    }
}

// ---------------------------------------------------------------------------
// Flow runtime actions (BE contract ourteam-backend#3641/#3643, PR #3657) —
// consumed by the ATS surfaces: Advance (checkpoints, #3075) and gate
// resolution for Knockout Flagged / Deadline Missed (#3073/#3074).

/**
 * HR resolution for a gate hold:
 * - `keep`        — waive the flag, flow resumes at the paused step
 * - `extend`      — deadline gates only; `extend_due` = new due ("+48h" | "same_day_17:00" | ISO)
 * - `next_intake` — Archived + re-entry date note (restorable)
 * - `archive`     — Archived (restorable)
 */
export interface FlowGateResolution {
    // `reroute` (#3086/BE #3662): knockout gates with a suggest_job_id only —
    // copies the application to the suggested job, archives the origin.
    action: "keep" | "extend" | "next_intake" | "archive" | "reroute";
    extend_due?: string;
    re_entry_date?: string;
}

/**
 * True when the request was aborted by OUR client timeout rather than answered
 * by the server (axios 1.x sets `ECONNABORTED`; some adapters use `ETIMEDOUT`).
 *
 * Worth distinguishing because every flow/advance action is idempotent on the
 * BE: a timeout means "we stopped listening", NOT "it did not happen". The
 * server routinely finishes the work anyway, so reporting it as a failure
 * sends HR to retry something that already succeeded — and on a call retake
 * that reads as the candidate never being reopened when they were.
 */
export const FLOW_TIMEOUT_TITLE = "Still processing — we stopped waiting for a reply";
export const FLOW_TIMEOUT_HINT =
    "The action may still have gone through. We've refreshed the data — check the candidate's stage before trying again.";

export function isClientTimeout(error: unknown): boolean {
    const e = error as { code?: string; response?: unknown } | null | undefined;
    if (!e) return false;
    // A real HTTP answer (any status) is never a client timeout.
    if (e.response) return false;
    return e.code === "ECONNABORTED" || e.code === "ETIMEDOUT";
}

/** POST /applications/{id}/flow/advance — resume a checkpointed candidate (→ Pending Assessment). */
export async function advanceApplicationFlow(applicationId: string): Promise<Record<string, unknown>> {
    const response: AxiosResponse = await axios({
        url: `${apiURL}/applications/${applicationId}/flow/advance`,
        method: "POST",
        headers: authHeaders(),
        // Same lesson as resolveFlowGate below: this does heavy work INLINE —
        // it resumes the flow, and approving a call retake reopens the AI
        // interview and has Maya message the candidate on WhatsApp. 15s aborted
        // those mid-flight (the request shows as "(canceled)" in devtools) even
        // though the BE went on to finish, so HR saw a failure for work that
        // had actually succeeded.
        timeout: 60000,
    });
    return response.data.data;
}

/** POST /applications/flow/advance-bulk */
export async function advanceApplicationFlowBulk(applicationIds: string[]): Promise<Record<string, unknown>[]> {
    const response: AxiosResponse = await axios({
        url: `${apiURL}/applications/flow/advance-bulk`,
        method: "POST",
        headers: authHeaders(),
        data: { application_ids: applicationIds },
        timeout: 30000,
    });
    return response.data.data || [];
}

// HR "Trigger Step" (#3242, BE ourteam-backend#3981): Maya (re-)opens a chosen
// step in the candidate's WhatsApp thread. Because of WhatsApp's 24h session
// window the BE answers in one of three modes, which the FE must render
// differently (done · waiting-for-consent · done-via-template).
export type TriggerStepMode = "asked_in_session" | "consent_template_sent" | "step_template_resent";

export interface TriggerStepResult {
    mode: TriggerStepMode;
    step_id: string;
    armed: boolean;
    // Out-of-window leg only: the consent-template dispatch outcome, surfaced
    // verbatim (skipped_wa_disabled / no opt-in / Twilio error). `sent: false`
    // means NO message reached the candidate — never show a success toast then.
    dispatch: { sent: boolean; reason?: string | null } | null;
}

// Dry-run read for the Follow up dialog (#3334, BE ourteam-backend#4179):
// the application's step list (from its PINNED flow version) + the verbatim
// WhatsApp text that would go out per step. Side-effect-free on the BE — same
// philosophy as jobs/hiring-flow/preview. Absent endpoint (older BE) ⇒ the
// dialog falls back to deriving the list from the job payload, no preview.
export interface TriggerStepPreviewStep {
    step_id: string;
    type: string;
    label: string;
    state: "completed" | "current" | "upcoming";
    preview?: {
        // Maya's actual (re-)ask — what goes out when HR sends as an
        // in-window (24h) message.
        within_24h?: { text?: string | null } | null;
        // The re-engage template leg — the FIRST message outside the window;
        // Maya continues with the step ask once the candidate replies.
        outside_24h?: { text?: string | null; template_event?: string | null } | null;
        // #3378 (BE ourteam-backend#4269): ai_interview steps only — the
        // rendered "Move to Phone Call" invitation template (HX84f05e…).
        move_to_phone_call?: { text?: string | null; template_event?: string | null } | null;
        // #3386 (BE ourteam-backend#4326): ai_interview steps only — the
        // rendered "Retry Call" couldn't-reach-you template (HX0b43ae…).
        retry_call?: { text?: string | null; template_event?: string | null } | null;
    } | null;
}

export interface TriggerStepPreviewResult {
    in_session: boolean;
    flow_version?: number;
    steps: TriggerStepPreviewStep[];
}

/** GET /applications/{id}/flow/trigger-step/preview */
export async function getTriggerStepPreview(applicationId: string): Promise<TriggerStepPreviewResult> {
    const response: AxiosResponse = await axios({
        url: `${apiURL}/applications/${applicationId}/flow/trigger-step/preview`,
        method: "GET",
        headers: authHeaders(),
        timeout: 15000,
    });
    return response.data.data as TriggerStepPreviewResult;
}

/** POST /applications/{id}/flow/trigger-step — body { step_id?, note?, is_send_24hour }; step_id defaults to the current step. */
export async function triggerFlowStep(
    applicationId: string,
    // is_send_24hour: HR's explicit choice, mirroring the "Send by template"
    // toggle position — true = the re-engage template leg, false = the direct
    // in-window (24h) message. Inverted-looking name kept per product decision.
    // interview_action (#3378/#3386): ai_interview steps only — "continue"
    // keeps today's behavior; "move_to_phone_call" and "retry_call" each send
    // their invitation template (pure template send, no LLM leg).
    payload: { step_id?: string; note?: string; is_send_24hour?: boolean; interview_action?: "continue" | "move_to_phone_call" | "retry_call" | "_invalid_system_follow_up" }
): Promise<TriggerStepResult> {
    const response: AxiosResponse = await axios({
        url: `${apiURL}/applications/${applicationId}/flow/trigger-step`,
        method: "POST",
        headers: authHeaders(),
        data: payload,
        timeout: 20000,
    });
    return response.data.data as TriggerStepResult;
}

/** POST /applications/{id}/flow/resolve-gate */
export async function resolveFlowGate(applicationId: string, resolution: FlowGateResolution): Promise<Record<string, unknown>> {
    const response: AxiosResponse = await axios({
        url: `${apiURL}/applications/${applicationId}/flow/resolve-gate`,
        method: "POST",
        headers: authHeaders(),
        data: resolution,
        // A resolution can do heavy work inline (reroute copies the
        // application; keep/extend resume the flow and may send WhatsApp) —
        // 15s produced client-side timeouts on real gates.
        timeout: 60000,
    });
    return response.data.data;
}

/** POST /applications/flow/resolve-gate-bulk */
export async function resolveFlowGateBulk(applicationIds: string[], resolution: FlowGateResolution): Promise<Record<string, unknown>[]> {
    const response: AxiosResponse = await axios({
        url: `${apiURL}/applications/flow/resolve-gate-bulk`,
        method: "POST",
        headers: authHeaders(),
        data: { ...resolution, application_ids: applicationIds },
        timeout: 30000,
    });
    return response.data.data || [];
}

/** Result of a WhatsApp reject (#3186 / BE ourteam-backend#3877, PR #3878). */
export interface WhatsappRejectResult {
    application_id?: string;
    status?: string;
    funnel_status?: string;
    wa_message_sid?: string;
    wa_status?: string;
}

/**
 * Reject a flagged candidate over WhatsApp — for email-less hiring-flow
 * candidates where the email reject can't reach them (#3186).
 *
 * Contract (BE ourteam-backend#3877, PR #3878 — LIVE on staging):
 *   POST /jobs/{job_id}/applicant/{applicant_id}/reject-whatsapp
 *     body { internal_note? }
 *     → 200 { application_id, status:"Rejected", funnel_status:"closed", wa_message_sid, wa_status }
 *   `applicant_id` is the candidate/user id (same param as start-interview).
 *   The candidate-facing text is the APPROVED `rejection` WhatsApp template
 *   (fixed; HR can't customise it) — the BE fills first_name/job_title/company —
 *   so there is NO `message` field. `internal_note` is optional and stored on the
 *   application (`reject_notes`). The reject only lands if the template send
 *   succeeds; on failure the candidate is NOT rejected and the error carries a
 *   `reason`. Errors: 404 (not opted in) · 409 (WhatsApp disabled / not
 *   Full-BYOC / already sent) · 502 (template send error). Works OUTSIDE the 24h
 *   window (template), so there is no "window closed" case. Field names come
 *   straight from the BE ticket — none invented.
 */
export async function rejectApplicantViaWhatsapp(
    jobId: string,
    applicantId: string,
    payload: { internal_note?: string }
): Promise<WhatsappRejectResult> {
    const response: AxiosResponse = await axios({
        url: `${apiURL}/jobs/${jobId}/applicant/${applicantId}/reject-whatsapp`,
        method: "POST",
        headers: authHeaders(),
        data: payload,
        timeout: 20000,
    });
    return response.data?.data ?? {};
}

// ---------------------------------------------------------------------------
// Candidate-side flow runner (#3077, BE #3642/#3644 in PR #3657). The apply
// page (`apply-v2.tsx`) is server-driven for custom-flow jobs: the ordered
// remaining steps come from GET /jobs/applications/{id}/pending-assessments,
// external_link steps confirm via POST …/flow/confirm-step, and Document
// Collection items upload via POST …/flow/documents (multipart). Candidate
// requests use the apply-link session token (tempAuthToken) fallback.

/** One remaining step of the candidate's flow, in server order. */
export interface PendingFlowStep {
    type: "form_assessment" | "ai_interview" | "eligibility_check" | "external_link" | "documents" | "human_interview" | string;
    label: string;
    optional: boolean;
    // Flow-runner fields — only set for custom-flow jobs (null on default jobs).
    step_id?: string | null;
    status?: string | null; // pending | started | completed
    is_current?: boolean | null;
    assessment_id?: string | null; // form / ai_interview steps
    url?: string | null; // external_link steps
    items?: Record<string, { received?: boolean }> | null; // documents checklist state
}

export interface PendingAssessmentsResponse {
    hasPendingAssessments: boolean;
    steps: PendingFlowStep[];
    current_step_id?: string | null; // null ⇒ default (non-flow) job
    flow_version?: number | null;
    /** Flow pause state (#3463, BE #4505 — final contract, BE PR #4506):
     *  "knockout_flagged" ⇒ an answer_knockout gate fired; "deadline_missed" ⇒ a
     *  step deadline passed. Either way the flow is paused at the current step
     *  pending an HR resolution (waive/extend flips it back to "active" with no
     *  extra FE bookkeeping). Absent/null (older BE payloads) or "active" ⇒ the
     *  flow runs normally. */
    flow_state?: "active" | "knockout_flagged" | "deadline_missed" | string | null;
}

function candidateAuthHeaders() {
    const token = getCookie("authToken") || getCookie("tempAuthToken");
    return {
        Authorization: `Bearer ${token}`,
    };
}

/** GET /jobs/applications/{id}/pending-assessments — the candidate's remaining steps in server order. */
export async function getPendingAssessments(applicationId: string): Promise<PendingAssessmentsResponse | null> {
    const response: AxiosResponse = await axios({
        url: `${apiURL}/jobs/applications/${applicationId}/pending-assessments`,
        method: "GET",
        headers: { "Content-Type": "application/json", ...candidateAuthHeaders() },
        timeout: 15000,
    });
    return response.data.data || null;
}

/** POST /jobs/applications/{id}/flow/confirm-step — candidate confirms an external_link step. */
export async function confirmFlowStep(applicationId: string, stepId: string): Promise<boolean> {
    const response: AxiosResponse = await axios({
        url: `${apiURL}/jobs/applications/${applicationId}/flow/confirm-step`,
        method: "POST",
        headers: { "Content-Type": "application/json", ...candidateAuthHeaders() },
        data: { step_id: stepId },
        timeout: 15000,
    });
    return response.data?.code === 200;
}

/** POST /jobs/applications/{id}/flow/documents — upload one Document Collection checklist item. */
export async function uploadFlowDocument(applicationId: string, stepId: string, item: string, file: File): Promise<boolean> {
    const formData = new FormData();
    formData.append("step_id", stepId);
    formData.append("item", item);
    formData.append("file", file);
    const response: AxiosResponse = await axios({
        url: `${apiURL}/jobs/applications/${applicationId}/flow/documents`,
        method: "POST",
        headers: { ...candidateAuthHeaders() },
        data: formData,
        timeout: 60000,
    });
    return response.data?.code === 200;
}

/** DELETE /hiring-flow/templates/{id} — company templates only (4xx on system rows). */
export async function deleteHiringFlowTemplate(templateId: string): Promise<boolean> {
    try {
        const response: AxiosResponse = await axios({
            url: `${apiURL}/hiring-flow/templates/${templateId}`,
            method: "DELETE",
            headers: authHeaders(),
            timeout: 15000,
        });
        return response.status >= 200 && response.status < 300;
    } catch (error) {
        catchErrorLog(`Failed to delete hiring-flow template: ${error}`);
        throw error;
    }
}


/** One outbound message of the builder's flow chat preview (#3257 / BE #3996). */
export interface FlowPreviewMessage {
    step_id?: string | null;
    step_type?: string | null;
    text: string;
}

/**
 * Dry-run preview of the WhatsApp conversation the chat-native runner would
 * send for a flow — the builder's Preview dialog renders these verbatim.
 *
 * Contract (BE ourteam-backend#3996 — the preview endpoint section; NOT yet
 * released; caller falls back to a local summary preview until it is):
 *   POST /jobs/hiring-flow/preview   { hiring_flow, job_title?, company_name? }
 *     → { code, data: { messages: [{ step_id, step_type, text }] } }  (ordered)
 * No side effects (nothing sent/stored). Returns null on any failure so the
 * caller can fall back. Field names come straight from the BE ticket.
 */
export interface FlowPreviewResult {
    messages: FlowPreviewMessage[];
    /** BE-reported gaps (e.g. an eligibility step with no fields context). */
    warnings: string[];
}

export async function previewFlowChat(payload: {
    hiring_flow: HiringFlow;
    job_title?: string;
    company_name?: string;
    /** Eligibility steps take their questions from this (or steps[].fields) — send
     *  the job form's live screening ticks so the preview shows every question. */
    work_authorization_metadata?: Record<string, boolean | undefined>;
    location_metadata?: unknown;
    is_enabled_wa_call?: boolean;
}): Promise<FlowPreviewResult | null> {
    try {
        const response: AxiosResponse = await axios({
            url: `${apiURL}/jobs/hiring-flow/preview`,
            method: "POST",
            headers: authHeaders(),
            data: payload,
            timeout: 15000,
        });
        const data = response.data?.data;
        const msgs = data?.messages;
        if (!Array.isArray(msgs)) return null;
        return {
            messages: msgs.filter((m: FlowPreviewMessage) => (m?.text || "").trim().length > 0),
            warnings: Array.isArray(data?.warnings) ? data.warnings.filter((w: unknown) => typeof w === "string" && w) : [],
        };
    } catch {
        return null;
    }
}
